@if (loading) {
  <div class="flex items-center justify-center py-10">
    <div class="loading loading-spinner loading-lg"></div>
  </div>
} @else if (loadError) {
  <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
    {{ loadError }}
  </div>
} @else {
  @if (hasTimelineItems) {
    @if (canManageTimeline || canCreateTemplate) {
      <div class="pl-6 flex flex-row justify-end gap-3">
        @if (canCreateTemplate) {
          <app-button
            [label]="'Create Template'"
            [style]="'secondary'"
            [icon]="FileTemplateIcon"
            (click)="createTemplateFromEvent()">
          </app-button>
        }
        @if (canManageTimeline) {
          <app-button [label]="'Add timeline item'" [style]="'primary'" [icon]="Plus" (click)="openRightSidebar('timeline-item-add')"></app-button>
        }
      </div>
    }

    @for (day of Object.keys(groupedTimelineItems); track day) {
      <div class="mt-4">
        <div class="pl-6 flex flex-row justify-between">
          <div class="text-xl">{{ day }}</div>
        </div>

        @for (item of groupedTimelineItems[day]; track item.id) {
          <div id="{{ item.id }}" class="flex w-full relative pl-8 sm:pl-32 py-6 group rounded-lg">
            <div
              class="flex flex-col w-full dark:bg-gray-800 sm:flex-row items-start mb-1 group-last:before:hidden

              before:absolute before:left-2 sm:before:left-0 before:h-full
              before:px-px before:bg-slate-300 sm:before:ml-[6.5rem] before:self-start before:-translate-x-1/2 before:translate-y-3

              after:absolute after:left-2 sm:after:left-0 after:w-4 after:h-4 after:bg-gray-800 after:border-4 after:box-content after:border-slate-50
              after:rounded-full sm:after:ml-[6.5rem] after:-translate-x-1/2 after:translate-y-0.5

              rounded bg-white border-gray-200 dark:border-gray-800 group-last:border-b-0 group-last:dark:border-b-0 p-6
            ">
              <time
                class="sm:absolute left-0 translate-y-0.5 inline-flex items-center justify-center text-xs font-semibold uppercase w-20 h-6 mb-3 sm:mb-0 rounded-full">
                {{ item.datetime | date: 'HH:mm' }}
              </time>

              <div class="flex flex-col gap-2 w-full">
                <div class="flex gap-4">
                  {{ item.name }}
                  @if (item.is_published) {
                    <app-badge [text]="'Published'" [color]="'success'"></app-badge>
                  }
                </div>

                <p class="text-gray-500 dark:text-gray-400">
                  {{ item.description }}
                </p>

                @if (canManageTimeline) {
                  <div class="absolute top-12 right-8 flex items-center gap-2 dark:bg-white/[0.03]">
                    <button title="Edit Item" class="text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500">
                      <lucide-icon class="hover:stroke-success-500" [size]="20" [name]="EditIcon" (click)="editTimelineItem(item)"></lucide-icon>
                    </button>
                    <button
                      [title]="hasProductsAttached(item) ? 'Cannot delete timeline item with products attached' : 'Delete Item'"
                      [disabled]="hasProductsAttached(item)"
                      class="text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-gray-500">
                      <lucide-icon (click)="requestDeleteTimelineItem(item)" [size]="20" [name]="TrashIcon"></lucide-icon>
                    </button>
                  </div>
                }

                @if (item.event_product_items && item.event_product_items.length > 0) {
                  <div class="mt-4 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead class="bg-gray-50 dark:bg-gray-800/50">
                        <tr>
                          <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Product
                          </th>
                          <th scope="col" class="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-48">
                            Category
                          </th>
                          <th scope="col" class="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                            Quantity
                          </th>
                          <th scope="col" class="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-40">
                            Price
                          </th>
                        </tr>
                      </thead>
                      <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        @for (productItem of item.event_product_items; track productItem.id) {
                          <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                            <td class="px-4 py-3">
                              <div class="flex flex-col gap-1 min-w-0">
                                <a
                                  [routerLink]="['/products', productItem.product.id, 'edit']"
                                  class="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 hover:underline cursor-pointer truncate">
                                  {{ productItem.product.name }}
                                </a>
                                @if (productItem.variant_selections && productItem.variant_selections.length > 0) {
                                  <div class="flex flex-col gap-1.5">
                                    @for (selection of productItem.variant_selections; track selection.id) {
                                      <div class="flex flex-col gap-0.5">
                                        <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 w-fit">
                                          {{ selection.variant?.name }}: {{ selection.variant_option?.name || 'Standard' }}
                                          @if (selection.quantity > 1) {
                                            <span class="ml-1 text-blue-600 dark:text-blue-400">×{{ selection.quantity }}</span>
                                          }
                                        </span>
                                        @if (selection.notes) {
                                          <div class="text-xs text-gray-600 dark:text-gray-400 italic pl-2 whitespace-pre-line">
                                            {{ selection.notes }}
                                          </div>
                                        }
                                      </div>
                                    }
                                  </div>
                                }
                              </div>
                            </td>
                            <td class="px-4 py-3 text-center">
                              <span [ngClass]="'text-' + ProductModelUtils.getCategoryColor(productItem.product.category) + '-600 dark:text-' + ProductModelUtils.getCategoryColor(productItem.product.category) + '-400'">
                                {{ productItem.product.category }}
                              </span>
                            </td>
                            <td class="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                              {{ productItem.quantity || 'N/A' }}
                            </td>
                            <td class="px-4 py-3 text-center">
                              <span class="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {{ getProductDisplayPrice(productItem) | currency:'GBP' }}
                              </span>
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    }
  } @else {
    <div class="min-h-[33vh] rounded-2xl border border-gray-200 bg-white p-8 dark:border-gray-800 dark:bg-white/[0.03] flex items-center justify-center">
      <div class="mx-auto max-w-xl text-center">
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white">No itinerary items yet</h3>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Add the first timeline item to start building this event run sheet.
        </p>
        @if (canManageTimeline) {
          <div class="mt-6">
            <app-button [label]="'Add timeline item'" [style]="'primary'" [icon]="Plus" (click)="openRightSidebar('timeline-item-add')"></app-button>
          </div>
        }
      </div>
    </div>
  }

  <app-right-sidebar [isSidebarOpen]="isRightSidebarOpen" (closed)="closeRightSidebar()">
    @if (canManageTimeline && rightSideBarContent == 'timeline-item-add') {
      <app-event-timeline-item-add [event]="event" (toggleOpen)="closeRightSidebar()" (itemCreated)="timelineItemCreated($event)"></app-event-timeline-item-add>
    }
    @if (canManageTimeline && rightSideBarContent == 'timeline-item-edit' && editingTimelineItem) {
      <app-event-timeline-item-edit [event]="event" [item]="editingTimelineItem" (toggleOpen)="closeRightSidebar()"
                                    (itemUpdated)="timelineItemUpdated($event)"></app-event-timeline-item-edit>
    }
    @if (canCreateTemplate && rightSideBarContent == 'create-template-from-event') {
      <app-create-template-from-event
        [event]="event"
        [timelineItems]="event.timeline_items || []"
        (closeModal)="closeRightSidebar()"
        (templateCreated)="onTemplateCreated()">
      </app-create-template-from-event>
    }
  </app-right-sidebar>


  @if (canManageTimeline) {
    <app-modal
      [title]="'Delete Timeline Item'"
      [content]="'Are you sure you want to delete this timeline item?'"
      [show]="!!deletingTimelineItem"
      [closeButtonLabel]="'Cancel'"
      [confirmButtonLabel]="'Yes, Proceed'"
      (onClose)="deletingTimelineItem = undefined"
      (onConfirm)="confirmDeleteTimelineItem()"
    ></app-modal>
  }
}
